/**
 * GRACE PERIOD UTILITY FUNCTIONS
 * 
 * Centralized grace period logic - DECOUPLED from authentication flow.
 * Auth validates credentials ONLY. UI layer enforces grace period.
 * 
 * Grace Period: 7 days from registration
 * Storage: user.user_metadata.grace_period_expires (ISO 8601 string)
 * 
 * @author JOEBOB MCALLIHAN
 * @date October 17, 2025
 */

import { User } from '@supabase/supabase-js';

// ===== CONSTANTS =====

export const GRACE_PERIOD_DAYS = 7;
export const RESEND_COOLDOWN_SECONDS = 60;

// ===== CORE FUNCTIONS =====

/**
 * Check if user's grace period has expired
 * @param user - Supabase user object
 * @returns true if grace period expired, false if still valid or no grace period set
 */
export function checkGracePeriodExpired(user: User | null): boolean {
  if (!user) return false;
  
  // Check user_metadata for grace_period_expires
  const gracePeriodExpires = user.user_metadata?.grace_period_expires;
  
  if (!gracePeriodExpires) {
    // No grace period set - user may already be verified
    return false;
  }
  
  try {
    const expirationDate = new Date(gracePeriodExpires);
    const now = new Date();
    
    // Expired if expiration date is in the past
    return expirationDate < now;
  } catch (error) {
    console.error('Error parsing grace period expiration:', error);
    return false; // Fail open - don't block user on parse error
  }
}

/**
 * Get remaining days in grace period
 * @param user - Supabase user object
 * @returns Number of days remaining, or null if no grace period or expired
 */
export function getRemainingGraceDays(user: User | null): number | null {
  if (!user) return null;
  
  const gracePeriodExpires = user.user_metadata?.grace_period_expires;
  
  if (!gracePeriodExpires) return null;
  
  try {
    const expirationDate = new Date(gracePeriodExpires);
    const now = new Date();
    
    if (expirationDate < now) {
      return 0; // Expired
    }
    
    const msPerDay = 1000 * 60 * 60 * 24;
    const diffMs = expirationDate.getTime() - now.getTime();
    const daysRemaining = Math.ceil(diffMs / msPerDay);
    
    return daysRemaining;
  } catch (error) {
    console.error('Error calculating remaining grace days:', error);
    return null;
  }
}

/**
 * Check if user is within grace period (not expired yet)
 * @param user - Supabase user object
 * @returns true if within grace period, false if expired or no grace period
 */
export function isWithinGracePeriod(user: User | null): boolean {
  if (!user) return false;
  
  const gracePeriodExpires = user.user_metadata?.grace_period_expires;
  
  if (!gracePeriodExpires) {
    // No grace period set - treat as verified
    return false;
  }
  
  try {
    const expirationDate = new Date(gracePeriodExpires);
    const now = new Date();
    
    // Within grace period if expiration is in the future
    return expirationDate > now;
  } catch (error) {
    console.error('Error checking grace period:', error);
    return false;
  }
}

/**
 * Check if user's email is verified
 * @param user - Supabase user object
 * @returns true if email confirmed, false otherwise
 */
export function isEmailVerified(user: User | null): boolean {
  if (!user) return false;
  
  // Supabase stores email confirmation in email_confirmed_at
  return !!user.email_confirmed_at;
}

/**
 * Get grace period expiration date
 * @param user - Supabase user object
 * @returns Date object of expiration, or null if no grace period set
 */
export function getGracePeriodExpiration(user: User | null): Date | null {
  if (!user) return null;
  
  const gracePeriodExpires = user.user_metadata?.grace_period_expires;
  
  if (!gracePeriodExpires) return null;
  
  try {
    return new Date(gracePeriodExpires);
  } catch (error) {
    console.error('Error parsing grace period expiration:', error);
    return null;
  }
}

/**
 * Format grace period expiration for display
 * @param user - Supabase user object
 * @returns Formatted string like "October 24, 2025" or null
 */
export function formatGracePeriodExpiration(user: User | null): string | null {
  const expirationDate = getGracePeriodExpiration(user);
  
  if (!expirationDate) return null;
  
  return expirationDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Get user-friendly grace period message
 * @param user - Supabase user object
 * @returns Object with message and urgency level
 */
export function getGracePeriodMessage(user: User | null): {
  message: string;
  urgency: 'none' | 'low' | 'medium' | 'high' | 'critical';
} {
  const remainingDays = getRemainingGraceDays(user);
  
  if (remainingDays === null) {
    return {
      message: 'Email verified',
      urgency: 'none',
    };
  }
  
  if (remainingDays === 0) {
    return {
      message: 'Grace period expired. Verify your email to continue.',
      urgency: 'critical',
    };
  }
  
  if (remainingDays === 1) {
    return {
      message: 'Last day! Verify your email to keep access.',
      urgency: 'critical',
    };
  }
  
  if (remainingDays <= 3) {
    return {
      message: `${remainingDays} days left. Please verify your email.`,
      urgency: 'high',
    };
  }
  
  if (remainingDays <= 5) {
    return {
      message: `${remainingDays} days remaining in grace period.`,
      urgency: 'medium',
    };
  }
  
  return {
    message: `${remainingDays} days to verify email.`,
    urgency: 'low',
  };
}

// ===== RESEND VERIFICATION TRACKING =====

// In-memory store for resend cooldowns (per session)
// Key: user ID, Value: timestamp of last resend
const resendCooldowns = new Map<string, number>();

/**
 * Check if user can resend verification email
 * @param userId - User ID
 * @returns true if cooldown expired, false if still in cooldown
 */
export function canResendVerification(userId: string): boolean {
  const lastResend = resendCooldowns.get(userId);
  
  if (!lastResend) return true;
  
  const now = Date.now();
  const cooldownMs = RESEND_COOLDOWN_SECONDS * 1000;
  
  return (now - lastResend) >= cooldownMs;
}

/**
 * Get remaining cooldown seconds for resend
 * @param userId - User ID
 * @returns Seconds remaining in cooldown, or 0 if can resend
 */
export function getResendCooldownRemaining(userId: string): number {
  const lastResend = resendCooldowns.get(userId);
  
  if (!lastResend) return 0;
  
  const now = Date.now();
  const cooldownMs = RESEND_COOLDOWN_SECONDS * 1000;
  const elapsedMs = now - lastResend;
  
  if (elapsedMs >= cooldownMs) return 0;
  
  const remainingMs = cooldownMs - elapsedMs;
  return Math.ceil(remainingMs / 1000);
}

/**
 * Mark that verification email was resent
 * @param userId - User ID
 */
export function markVerificationResent(userId: string): void {
  resendCooldowns.set(userId, Date.now());
}

/**
 * Clear resend cooldown for user (e.g., on logout)
 * @param userId - User ID
 */
export function clearResendCooldown(userId: string): void {
  resendCooldowns.delete(userId);
}

// ===== INTEGRATION HELPERS =====

/**
 * Should show grace period blocker screen?
 * @param user - Supabase user object
 * @returns true if should show blocker (expired + unverified), false otherwise
 */
export function shouldShowGracePeriodBlocker(user: User | null): boolean {
  if (!user) return false;
  
  // Check if email already verified
  if (isEmailVerified(user)) return false;
  
  // Check if grace period expired
  return checkGracePeriodExpired(user);
}

/**
 * Should show grace period warning banner?
 * @param user - Supabase user object
 * @returns true if should show warning (within grace period but not verified)
 */
export function shouldShowGracePeriodWarning(user: User | null): boolean {
  if (!user) return false;
  
  // Don't show if already verified
  if (isEmailVerified(user)) return false;
  
  // Show warning if within grace period
  return isWithinGracePeriod(user);
}

/**
 * Calculate grace period expiration from registration date
 * @param registrationDate - Date user registered
 * @returns ISO 8601 string of expiration date
 */
export function calculateGracePeriodExpiration(registrationDate: Date = new Date()): string {
  const expirationDate = new Date(registrationDate);
  expirationDate.setDate(expirationDate.getDate() + GRACE_PERIOD_DAYS);
  return expirationDate.toISOString();
}

// ===== EXPORTS =====

export default {
  // Core checks
  checkGracePeriodExpired,
  getRemainingGraceDays,
  isWithinGracePeriod,
  isEmailVerified,
  getGracePeriodExpiration,
  formatGracePeriodExpiration,
  getGracePeriodMessage,
  
  // Resend verification
  canResendVerification,
  getResendCooldownRemaining,
  markVerificationResent,
  clearResendCooldown,
  
  // Integration helpers
  shouldShowGracePeriodBlocker,
  shouldShowGracePeriodWarning,
  calculateGracePeriodExpiration,
  
  // Constants
  GRACE_PERIOD_DAYS,
  RESEND_COOLDOWN_SECONDS,
};
